import { ConnectError } from '@bufbuild/connect';
import cn from 'classnames';
import { FC, useCallback, useEffect, useState } from 'react';
import { FileRejection, FileWithPath, useDropzone } from 'react-dropzone';
import { useIntl } from 'react-intl';

import { AssetType } from '@/shared/api/protocol-ts/model/dto_asset_pb';
import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import {
  ACCEPTED_FILE_TYPES_FOR_DROPZONE_BY_ASSET_TYPE,
  UPLOADING_IMAGE_MAX_FILES,
} from '@/shared/config';
import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { InlineFormError, Modal } from '@/shared/ui';

import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';
import { patientModel } from '@/entities/patient';

import { errorToastCaller } from '@/features/errorToastCaller';
import { useUploadAssetContext } from '@/features/uploadAsset/index.ts';

import { getUploadDentalPhotosErrorMessage } from '../../lib/getUploadDentalPhotosErrorMessage.ts';
import { validateDentalMediaFileSize } from '../../lib/validateDentalMediaFileSize.ts';
import { UploadDentalPhotosDropzone } from '../UploadDentalPhotosDropzone/UploadDentalPhotosDropzone.tsx';
import { UploadPhotoFile } from '../UploadPhotoFile/UploadPhotoFile.tsx';

import styles from './UploadDentalPhotosModal.module.scss';

const DENTAL_PHOTO_WIDGET_SCROLL_KEY = 'dental-photo-widget-scroll-target';

type UploadDentalPhotosModalProps = {
  patientID: string;
  className?: string;
  testID?: string;
};

export const UploadDentalPhotosModal: FC<UploadDentalPhotosModalProps> = (
  props,
) => {
  const { className, testID, patientID } = props;

  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();
  const { startUploadAsset: startUploadPhoto } = useUploadAssetContext();

  const [files, setFiles] = useState<FileWithPath[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);

  const patientFullName = useAppSelector(
    patientModel.selectors.selectPatientFullName(patientID),
  );

  const { visible, data } = useAppSelector(
    modalModel.selectors.selectDentalPhotosUploadModal,
  );

  const isForbidUploadsFromWeb = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Forbid_UploadsFromWeb,
    ),
  );

  const preSelectedAlbumID = data?.albumID;

  const resolveAlbumID = (): string | undefined => preSelectedAlbumID;

  const handleUpload = async () => {
    setIsUploading(true);

    try {
      const targetAlbumID = resolveAlbumID();

      trackEvent({
        eventName: targetAlbumID
          ? 'DentalPhotosAddPhotoToAlbumClicked'
          : 'DentalPhotosUploadButtonClicked',
        properties: {
          album_id: targetAlbumID,
        },
      });

      dispatch(modalModel.actions.closeModal(ModalID.DentalPhotosUpload));

      await startUploadPhoto({
        patientID,
        files,
        assetType: AssetType.AssetType_Study_DentalPhoto,
        albumID: targetAlbumID,
        meta: {
          studyType: AssetType.AssetType_Study_DentalPhoto,
          patientName: patientFullName,
          albumID: targetAlbumID,
        },
      });

      if (data?.scrollToWidgetOnUpload) {
        sessionStorage.setItem(DENTAL_PHOTO_WIDGET_SCROLL_KEY, patientID);
      }

      setFiles([]);
    } catch (error) {
      errorToastCaller(error as ConnectError);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    dispatch(modalModel.actions.closeModal(ModalID.DentalPhotosUpload));
  };

  const handleAcceptedFiles = useCallback((acceptedFiles: FileWithPath[]) => {
    setErrorMessage('');
    setFiles((prevFiles) => prevFiles.concat(acceptedFiles));
  }, []);

  const handleCameraCapture = useCallback((capturedFiles: File[]) => {
    setFiles((prevFiles) => prevFiles.concat(capturedFiles as FileWithPath[]));
  }, []);

  const handleRejectFiles = useCallback(
    (fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        setErrorMessage(
          getUploadDentalPhotosErrorMessage(fileRejections, formatMessage),
        );
      }
    },
    [formatMessage],
  );

  const dropzoneState = useDropzone({
    maxFiles: UPLOADING_IMAGE_MAX_FILES,
    validator: validateDentalMediaFileSize,
    accept:
      ACCEPTED_FILE_TYPES_FOR_DROPZONE_BY_ASSET_TYPE[
        AssetType.AssetType_Study_DentalPhoto
      ],
    onDropRejected: handleRejectFiles,
    onDropAccepted: handleAcceptedFiles,
  });

  useEffect(() => {
    setFiles([]);
    setErrorMessage('');
  }, [visible]);

  const isUploadDisabled = files.length === 0 || isUploading;

  if (isForbidUploadsFromWeb || isMobile) {
    return null;
  }

  return (
    <Modal
      data-component="upload-dental-photos-modal"
      isOpen={visible}
      title={formatMessage({
        id: 'dentalPhotos.upload.title',
        defaultMessage: 'Upload photos or videos',
      })}
      okButtonProps={{ disabled: isUploadDisabled, loading: isUploading }}
      okButtonText={formatMessage({
        id: 'global.upload',
        defaultMessage: 'Upload',
      })}
      onOk={handleUpload}
      onCancel={handleClose}
      shouldRenderCancelButton
    >
      <div data-testid={testID} className={cn(styles.container, className)}>
        {files.length > 0 &&
          files.map((file) => (
            <UploadPhotoFile
              key={`${file.name}-${file.type}-${file.size}`}
              file={file}
              onDelete={() =>
                setFiles((prevFiles) =>
                  prevFiles.filter((prevFile) => prevFile.name !== file.name),
                )
              }
            />
          ))}

        <UploadDentalPhotosDropzone
          dropzoneState={dropzoneState}
          onCameraCapture={handleCameraCapture}
        />

        {!!errorMessage && <InlineFormError errorMessage={errorMessage} />}
      </div>
    </Modal>
  );
};
